import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";

import type { FeatureAccessMap } from "@/lib/feature-flags";
import type { Session } from "@/lib/session";

/** What the `/_app` guard resolves: the signed-in session and its feature map (null when signed out). */
export type AppGuard = { session: Session | null; features: FeatureAccessMap | null };

type AppGuardDeps = {
  getSession: (headers: Headers) => Promise<Session>;
  getFeatureAccess: (
    userId: string,
    roleId: number | null | undefined,
  ) => Promise<FeatureAccessMap>;
  reportError: (error: Error) => void;
};

/**
 * The guard's logic, with its dependencies injected so tests can drive it
 * without a database. The session is resolved once per request and handed to
 * the feature lookup, which needs its user id and role.
 */
export function createAppGuardResolver(deps: AppGuardDeps) {
  return async (headers: Headers): Promise<AppGuard> => {
    const session = await deps.getSession(headers);

    if (!session) return { session: null, features: null };

    try {
      return {
        session,
        features: await deps.getFeatureAccess(session.user.id, session.user.roleId),
      };
    } catch (error) {
      // The layout must never go down with a flag lookup (for instance a
      // database that has not run the flags migration yet): fail closed, so
      // every flagged feature stays hidden and the rest of the app works.
      deps.reportError(error instanceof Error ? error : new Error(String(error)));

      return { session, features: null };
    }
  };
}

/**
 * Session and feature access for the `/_app` guard, in one server call. It is
 * not cached in the browser: a revoked session or a changed flag applies on the
 * next navigation. A server fn, so it resolves during SSR (direct call) and over
 * RPC on client navigations. Keep this module free of server-only imports (it is
 * bundled to the browser as an RPC stub).
 */
export const getAppGuard = createServerFn({ method: "GET" }).handler(
  async (): Promise<AppGuard> => {
    const [{ getSessionFromHeaders }, { getFeatureAccess }, { log }] = await Promise.all([
      import("@/lib/session.server"),
      import("@/lib/feature-flags.server"),
      import("@/lib/evlog"),
    ]);

    const resolve = createAppGuardResolver({
      getSession: getSessionFromHeaders,
      getFeatureAccess,
      reportError: (error) => log.error(error),
    });

    return resolve(getRequest().headers);
  },
);
